import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, LayoutGrid, Package, Search } from 'lucide-react';
import { Button, EmptyState, ErrorState, Skeleton, SubBar } from '@/design/components';
import { cn } from '@/lib/cn';
import { useMe } from '@/auth/useMe';
import { usePlanograms } from './admin/planogramApi';
import {
  CATALOG_PAGE_SIZE,
  useCatalog,
  useCatalogCategories,
  useImageUrl,
  type CatalogRow,
} from './api';

/**
 * ארכיון המוצרים שכל עובד יכול לחפש בו (PRD 5.7.4).
 *
 * החיפוש בשרת ובעמודים (מיגרציה 089). קודם ירד כל הקטלוג לטלפון
 * כדי לסנן אותו בדפדפן, וזה היה בסדר כל עוד היו בו שלושה עשר
 * מוצרי דמה.
 */
export function ProductsPage() {
  const { me } = useMe();
  // הקישור לפלנוגרמה מופיע רק אם יש מה לראות בסניף, כדי שלא
  // ננווט את העובד למסך ריק
  const planograms = usePlanograms(me?.primaryBranchId ?? null);
  const categories = useCatalogCategories();

  const [term, setTerm] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const catalog = useCatalog(term, categoryId, page);

  const total = catalog.data?.total ?? 0;
  const rows = catalog.data?.rows ?? [];
  const shownSoFar = page * CATALOG_PAGE_SIZE + rows.length;
  const isFiltered = term.trim() !== '' || categoryId !== null;

  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  return (
    <div className="grid content-start gap-4">
      <SubBar title="מוצרים" subtitle="מה אנחנו מוכרים, ומה חשוב להגיד" />

      <div className="grid gap-4 px-4 pb-8">
        {(planograms.data?.length ?? 0) > 0 && (
          <Link
            to="/products/planogram"
            className="flex items-center gap-3 rounded-md border border-line bg-raised p-3 no-underline"
          >
            <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-olive-soft">
              <LayoutGrid size={20} strokeWidth={1.75} aria-hidden="true" className="text-ink" />
            </span>
            <span className="grid min-w-0 grow gap-0.5">
              <span className="text-title font-medium text-ink">פלנוגרמה</span>
              <span className="text-label text-muted">איך המדפים אמורים להיראות בסניף שלך</span>
            </span>
            <ChevronLeft
              size={20}
              strokeWidth={1.75}
              aria-hidden="true"
              className="shrink-0 text-muted"
            />
          </Link>
        )}

        <div className="relative">
          {/* RTL: האייקון ב start, והריפוד נפתח מאותו צד */}
          <Search
            size={20}
            strokeWidth={1.75}
            aria-hidden="true"
            className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            type="search"
            value={term}
            onChange={(event) => changeFilter(() => setTerm(event.target.value))}
            aria-label="חיפוש מוצר"
            placeholder="שם, מק״ט או מחלקה"
            className="min-h-11 w-full rounded-sm border border-muted bg-surface ps-10 pe-3 text-body text-ink"
          />
        </div>

        {(categories.data?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2">
            <Chip active={categoryId === null} onClick={() => changeFilter(() => setCategoryId(null))}>
              הכל
            </Chip>
            {(categories.data ?? []).map((category) => (
              <Chip
                key={category.id}
                active={categoryId === category.id}
                onClick={() => changeFilter(() => setCategoryId(category.id))}
              >
                {category.name}
              </Chip>
            ))}
          </div>
        )}

        {catalog.isPending && <Skeleton className="h-40" />}
        {catalog.isError && <ErrorState onRetry={() => void catalog.refetch()} />}

        {catalog.data && total === 0 && (
          <EmptyState
            title={isFiltered ? 'לא נמצא מוצר' : 'אין עדיין מוצרים'}
            description={
              isFiltered
                ? 'אפשר לנסות מילה אחרת או מחלקה אחרת.'
                : 'כשההנהלה תוסיף מוצרים הם יופיעו כאן.'
            }
          />
        )}

        <ul className="m-0 grid list-none gap-2 p-0">
          {rows.map((product) => (
            <li key={product.id}>
              <ProductRow product={product} />
            </li>
          ))}
        </ul>

        {/* "עוד" ולא גלילה אינסופית: ברשימה שמתארכת לבד אי אפשר
            להגיע לסוף, ואין לאן לחזור אחרי פתיחת מוצר */}
        {shownSoFar < total && (
          <Button variant="secondary" onClick={() => setPage((current) => current + 1)}>
            עוד {Math.min(CATALOG_PAGE_SIZE, total - shownSoFar)} מוצרים
          </Button>
        )}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'min-h-11 cursor-pointer rounded-pill border px-4 text-label font-medium',
        active ? 'border-olive bg-olive text-on-olive' : 'border-muted bg-transparent text-ink',
      )}
    >
      {children}
    </button>
  );
}

function ProductRow({ product }: { product: CatalogRow }) {
  const image = useImageUrl(product.image_path);

  return (
    <Link
      to={`/products/${product.id}`}
      className="flex items-center gap-3 rounded-md border border-line bg-raised p-3 no-underline"
    >
      <span className="grid size-14 shrink-0 place-items-center overflow-hidden rounded-sm bg-olive-soft">
        {image.data ? (
          <img src={image.data} alt="" className="size-full object-cover" />
        ) : (
          <Package size={24} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        )}
      </span>

      <span className="grid min-w-0 grow gap-0.5">
        <span className="truncate text-title font-medium text-ink">{product.name}</span>
        {product.categories.length > 0 && (
          <span className="truncate text-label text-muted">
            {product.categories.map((category) => category.name).join(' · ')}
          </span>
        )}
      </span>

      <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0 text-muted" />
    </Link>
  );
}
